import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

export interface Tab {
  /** Unique identifier for the tab */
  id: string | number;
  /** Tab label */
  label: string;
  /** Tab content */
  content: React.ReactNode;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Optional badge count */
  badge?: number;
}

export interface TabsProps {
  /** Array of tab items */
  tabs: Tab[];
  /** Default active tab ID */
  defaultTab?: string | number;
  /** Controlled active tab ID */
  activeTab?: string | number;
  /** Callback when tab changes */
  onChange?: (tabId: string | number) => void;
  /** Custom className for tabs container */
  className?: string;
  /** Variant style */
  variant?: 'default' | 'pills';
}

export const handleTabChangeForProps = (
  tabs: Tab[],
  tabId: string | number,
  isControlled: boolean,
  setInternalActiveTab: (tabId: string | number) => void,
  onChange?: (tabId: string | number) => void
): void => {
  const tab = tabs.find((t) => t.id === tabId);
  if (!tab || tab.disabled) return;

  if (!isControlled) {
    setInternalActiveTab(tabId);
  }
  onChange?.(tabId);
};

/**
 * Tabs component with keyboard navigation
 *
 * Features:
 * - Arrow key navigation (Left/Right)
 * - Home/End keys to jump to first/last tab
 * - Accessible with ARIA attributes (tablist, tab, tabpanel)
 * - Controlled and uncontrolled modes
 * - Optional badges and icons
 *
 * @example
 * ```tsx
 * <Tabs
 *   tabs={[
 *     { id: 'overview', label: 'Overview', content: <OverviewContent /> },
 *     { id: 'settings', label: 'Settings', content: <SettingsContent /> },
 *   ]}
 *   defaultTab="overview"
 *   onChange={(tabId) => console.log('Active tab:', tabId)}
 * />
 * ```
 */
export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  className,
  variant = 'default',
}) => {
  const isControlled = controlledActiveTab !== undefined;
  const [internalActiveTab, setInternalActiveTab] = useState<string | number>(
    defaultTab || tabs[0]?.id || ''
  );

  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
  const tabRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());

  // Get non-disabled tabs for keyboard navigation
  const navigableTabs = tabs.filter((tab) => !tab.disabled);
  const activeTabIndex = navigableTabs.findIndex((tab) => tab.id === activeTab);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.getAttribute('role') !== 'tab') return;

      let newIndex = activeTabIndex;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = activeTabIndex - 1;
          if (newIndex < 0) newIndex = navigableTabs.length - 1;
          break;

        case 'ArrowRight':
          e.preventDefault();
          newIndex = activeTabIndex + 1;
          if (newIndex >= navigableTabs.length) newIndex = 0;
          break;

        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;

        case 'End':
          e.preventDefault();
          newIndex = navigableTabs.length - 1;
          break;

        default:
          return;
      }

      const newTab = navigableTabs[newIndex];
      if (newTab) {
        handleTabChange(newTab.id);
        tabRefs.current.get(newTab.id)?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeTabIndex, navigableTabs]);

  const handleTabChange = (tabId: string | number) => {
    handleTabChangeForProps(
      tabs,
      tabId,
      isControlled,
      setInternalActiveTab,
      onChange
    );
  };

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  const variantClasses = {
    default: {
      container: 'border-b border-gray-200',
      tab: 'px-4 py-2 border-b-2 -mb-px',
      active: 'border-blue-600 text-blue-600',
      inactive: 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300',
      disabled: 'border-transparent text-gray-400 cursor-not-allowed',
    },
    pills: {
      container: 'bg-gray-100 rounded-lg p-1',
      tab: 'px-4 py-2 rounded-md',
      active: 'bg-white text-gray-900 shadow-sm',
      inactive: 'text-gray-600 hover:text-gray-900',
      disabled: 'text-gray-400 cursor-not-allowed',
    },
  };

  const styles = variantClasses[variant];

  return (
    <div className={cn('w-full', className)} data-testid="tabs">
      {/* Tab List */}
      <div
        role="tablist"
        aria-label="Tabs"
        className={cn('flex gap-1', styles.container)}
        data-testid="tabs-list"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const isDisabled = tab.disabled;

          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) {
                  tabRefs.current.set(tab.id, el);
                } else {
                  tabRefs.current.delete(tab.id);
                }
              }}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              aria-disabled={isDisabled}
              tabIndex={isActive && !isDisabled ? 0 : -1}
              disabled={isDisabled}
              className={cn(
                'font-medium text-sm transition-colors flex items-center gap-2 whitespace-nowrap',
                styles.tab,
                isActive && !isDisabled
                  ? styles.active
                  : isDisabled
                  ? styles.disabled
                  : styles.inactive,
                !isDisabled && 'cursor-pointer'
              )}
              onClick={() => handleTabChange(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon && <span className="flex-shrink-0">{tab.icon}</span>}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge > 0 && (
                <span
                  className={cn(
                    'px-2 py-0.5 text-xs rounded-full',
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-200 text-gray-700'
                  )}
                  data-testid={`tab-badge-${tab.id}`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Panels */}
      <div className="mt-4">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;

          return (
            <div
              key={tab.id}
              role="tabpanel"
              id={`tabpanel-${tab.id}`}
              aria-labelledby={`tab-${tab.id}`}
              hidden={!isActive}
              tabIndex={0}
              className={cn(!isActive && 'hidden')}
              data-testid={`tabpanel-${tab.id}`}
            >
              {isActive && tab.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

Tabs.displayName = 'Tabs';
